/**
 * Post-sign-in routing. A completed sign-in has an IDENTITY but no firm yet, so it goes to the
 * firm picker (`/firms`) — EXCEPT when the sign-in was started from:
 *  - an invitation, which it returns to (`/invite/<token>`) so the invitee can accept on the
 *    session they just proved; or
 *  - a firm-scoped screen that sent the user to sign in again (today only `/settings/security`,
 *    when worker-auth refused a TOTP change with `second_factor_required`). A fresh sign-in has
 *    no firm bound, so that return goes THROUGH the picker (`/firms?redirect=…`), which continues
 *    to the screen once a firm session exists.
 *
 * The `redirect` search param is attacker-reachable (it rides in a URL), so it is honoured ONLY
 * when it is exactly an in-app invite path or exactly one of the allowlisted return paths;
 * anything else — an absolute or protocol-relative URL, another route — is ignored in favour of
 * `/firms`. That makes it impossible to use as an open redirect and keeps firm-scoped
 * destinations behind the picker.
 */
import { useNavigate } from '@tanstack/react-router';
import { useCallback } from 'react';

// One path segment after /invite/ — no further `/`, no query/fragment, no backslash.
const INVITE_PATH = /^\/invite\/([^/?#\\]+)$/;

/** Firm-scoped screens a re-sign-in may return to (exact match only — never a prefix). */
const RETURN_PATHS = ['/settings/security'] as const;
export type ReturnPath = (typeof RETURN_PATHS)[number];

/** The invite token if `redirect` is exactly `/invite/<token>`, else null. */
export function inviteTokenOf(redirect: unknown): string | null {
  if (typeof redirect !== 'string') return null;
  const match = INVITE_PATH.exec(redirect);
  if (!match) return null;
  try {
    return decodeURIComponent(match[1]!);
  } catch {
    return null;
  }
}

/** The allowlisted firm-scoped return path if `redirect` is exactly one, else null. */
export function returnPathOf(redirect: unknown): ReturnPath | null {
  return RETURN_PATHS.find((path) => path === redirect) ?? null;
}

/** The `redirect` value that brings a sign-in back to this invitation. */
export function inviteRedirect(token: string): string {
  return `/invite/${encodeURIComponent(token)}`;
}

/** The search params to carry a valid redirect (invite or return path) across the sign-in steps; `{}` otherwise. */
export function carriedRedirect(redirect: unknown): { redirect?: string } {
  const token = inviteTokenOf(redirect);
  if (token !== null) return { redirect: inviteRedirect(token) };
  const back = returnPathOf(redirect);
  return back === null ? {} : { redirect: back };
}

/**
 * Navigate where a completed sign-in belongs: the originating invite; else the firm picker —
 * carrying an allowlisted return path for the picker to continue to after a firm is selected.
 */
export function useAfterSignIn(): (redirect: unknown) => Promise<void> {
  const navigate = useNavigate();
  return useCallback(
    async (redirect: unknown) => {
      const token = inviteTokenOf(redirect);
      if (token !== null) {
        await navigate({ to: '/invite/$token', params: { token } });
        return;
      }
      const back = returnPathOf(redirect);
      await navigate({ to: '/firms', search: back === null ? {} : { redirect: back } });
    },
    [navigate],
  );
}
